import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@0xtorch/evm'
import { createSolanaActionSource } from '@0xtorch/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  bybitAccountCategory,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v5/asset/withdraw/query-record',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // Sort by createTime in ascending order (as per API specification)
  const sortedWithdrawals = response.result.rows.sort(
    (a, b) => Number(a.createTime) - Number(b.createTime),
  )

  // データ処理
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedWithdrawals) {
    // Skip processing if status is 'CancelByUser', 'Reject', or 'Fail'
    if (['CancelByUser', 'Reject', 'Fail'].includes(item.status)) {
      continue
    }

    // Stop processing if status is 'SecurityCheck', 'Pending', 'BlockchainConfirmed', 'MoreInformationRequired', or 'Unknown'
    if (
      [
        'SecurityCheck',
        'Pending',
        'BlockchainConfirmed',
        'MoreInformationRequired',
        'Unknown',
      ].includes(item.status)
    ) {
      stopped = true
      break
    }

    const createTime = Number(item.createTime)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? createTime : Math.min(fetchedFrom, createTime)
    fetchedTo =
      fetchedTo === undefined ? createTime : Math.max(fetchedTo, createTime)

    // Create fee action if withdrawFee > 0
    if (item.withdrawFee.value > 0n) {
      const asset = getAsset(item.coin)
      if (!asset) {
        throw new AssetNotFoundException(item.coin)
      }

      actions.push({
        comment: `Withdraw ${toStringBigDecimal(item.amount)} [${item.coin}] | chain: ${item.chain} | tx: ${item.txID}`,
        evidence: 'system-rule',
        order: 0,
        source: `${bybitAccountCategory}_${config.type}_${item.withdrawId}`,
        type: 'fee',
        timestamp: createTime,
        transfers: [
          {
            asset,
            amount: item.withdrawFee,
            direction: 'out',
          },
        ],
      })
    }

    // Create EVM source if applicable
    if (item.chain && item.txID) {
      const evmChainId = networkEvmChainIds[item.chain]
      if (evmChainId && isHex(item.txID)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: item.txID,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.chain === solanaNetwork && item.txID) {
      sources.push(createSolanaActionSource({ signature: item.txID }))
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.result.rows.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
